import { Children, FC, isValidElement, MouseEvent, ReactElement, ReactNode } from 'react';

import { Box, Button, HStack, Menu, MenuButton, MenuItem, MenuList, Portal, rem, Text, VStack } from '@bonusly/design-system';

import { Reveal } from '../../../components/display/reveal';

interface UserNavMenuProps {
	buttonIcon?: ReactElement;
	buttonContents?: ReactNode;
	buttonText?: string;
	children: ReactNode;
	isExpanded: boolean;
	/** When true, clicking this menu button won't close the parent menu */
	isSubmenu?: boolean;
}

export const UserNavMenu = ({ buttonIcon, buttonContents, buttonText, children, isExpanded, isSubmenu }: UserNavMenuProps) => {
	// Prevent clicks from propagating to parent menu when this is a submenu
	const handleSubmenuClick = (e: MouseEvent) => {
		if (isSubmenu) {
			e.stopPropagation();
		}
	};

	// Common layout styles (work for both Button and Box)
	const commonStyles = {
		px: rem(10),
		py: rem(8),
		h: rem(40),
		w: isExpanded ? '100%' : rem(40),
		borderRadius: 'md',
		justifyContent: isExpanded ? 'flex-start' : 'center',
		_active: { bg: 'secondary.bgAlpha' },
		_hover: { bg: 'secondary.bgAlpha' },
		_focusVisible: { outline: '2px solid', outlineColor: 'blue.500', borderRadius: '1px' },
	};

	// Button-specific styles (only used when rendering as Button)
	const buttonStyles = {
		variant: 'ghost' as const,
		colorScheme: 'secondary' as const,
		size: 'sm' as const,
	};

	// Box-specific styles (only used when rendering as Box for submenus)
	const boxStyles = {
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
	};

	const menuContent = (
		<Menu placement="right">
			{/* When used as a submenu, render as Box to avoid nested buttons (MenuItem is already a button) */}
			<MenuButton
				onClick={handleSubmenuClick}
				as={isSubmenu ? Box : Button}
				{...commonStyles}
				{...(isSubmenu ? boxStyles : buttonStyles)}
				data-testid="user-nav-menu-button"
			>
				<HStack spacing={rem(12)} justifyContent={isExpanded ? 'flex-start' : 'center'}>
					{buttonIcon}
					<Reveal if={isExpanded && buttonText}>
						<Text color="color.default" fontSize={100} fontWeight="medium">
							{buttonText}
						</Text>
					</Reveal>
					<Reveal if={isExpanded && buttonContents}>{buttonContents}</Reveal>
				</HStack>
			</MenuButton>
			<Portal>
				<MenuList py={rem(6)} px={rem(4)} bgColor="bg.surface">
					<VStack spacing={rem(4)} align="stretch" bgColor="bg.surface">
						{Children.toArray(children)
							// NOTE: hide any falsy children (e.g. anything that is undefined or null that the user does not have access to)
							.filter(Boolean)
							.map((child, index) => {
								// Check if the child is a submenu (UserNavMenu with isSubmenu prop)
								const isChildSubmenu = isValidElement(child) && child.props?.isSubmenu;
								// Check if the child is a divider (UserNavDivider component)
								const isChildDivider = isValidElement(child) && (child.type as FC & { isNavDivider?: boolean })?.isNavDivider;

								// Dividers render without MenuItem wrapper
								if (isChildDivider) {
									return <Box key={index}>{child}</Box>;
								}

								// For submenus, wrap in MenuItem but click the MenuButton inside to open
								if (isChildSubmenu) {
									const handleSubmenuKeyboardOpen = (e: MouseEvent<HTMLButtonElement>) => {
										if (e.detail !== 0) return;
										const menuButton = e.currentTarget.querySelector('[data-testid="user-nav-menu-button"]') as HTMLButtonElement;
										menuButton?.click();
									};

									return (
										<MenuItem
											key={index}
											bgColor="bg.surface"
											p={0}
											_hover={{ bg: 'none', border: 'none' }}
											_focusVisible={{ bg: 'none', border: '2px solid', borderColor: 'blue.500' }}
											onClick={handleSubmenuKeyboardOpen}
											closeOnSelect={false}
											data-testid={`user-nav-menu-item-${index}`}
										>
											{child}
										</MenuItem>
									);
								}

								//  NOTE: this is for keyboard navigation since the clickable element is on the child (UserNavLink or UserNavAction), not the MenuItem
								//  e.detail === 0 indicates a keyboard-triggered click (Enter/Space), while e.detail >= 1 indicates a mouse click
								//  For mouse clicks, the child's onClick already fired, so we skip to avoid duplicate navigation
								const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
									if (e.detail !== 0) return;
									// Try anchor first (UserNavLink), then button (UserNavAction)
									const clickable = e.currentTarget.querySelector('a') ?? e.currentTarget.querySelector('button');
									clickable?.click();
								};

								return (
									<MenuItem
										key={index}
										bgColor="bg.surface"
										p={0}
										_hover={{ bg: 'none', border: 'none' }}
										_focusVisible={{ bg: 'none', border: '2px solid', borderColor: 'blue.500' }}
										onClick={handleClick}
										data-testid={`user-nav-menu-item-${index}`}
									>
										{child}
									</MenuItem>
								);
							})}
					</VStack>
				</MenuList>
			</Portal>
		</Menu>
	);

	// Wrap in a Box to prevent MenuItem from treating clicks on this as a menu selection
	if (isSubmenu) {
		return (
			<Box w="100%" onClick={handleSubmenuClick}>
				{menuContent}
			</Box>
		);
	}

	return menuContent;
};
